"use client";
import Link from "next/link";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import React from "react";
import {
  FEATURES_PATH,
  MARKETING_FEATURE_NAV_ITEMS,
} from "@/lib/marketing-feature-nav-items";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Menu,
  X,
  Lightbulb,
  Link2,
  FileText,
  LayoutGrid,
  MessagesSquare,
  PieChart,
  Activity,
  ScrollText,
  Briefcase,
  BarChart3,
  Newspaper,
  NotebookPen,
  Gauge,
  BookOpen,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { Container } from "@/components/container";
import {
  MARKETING_RESOURCE_NAV_ITEMS,
  type MarketingResourceNavItem,
} from "@/lib/marketing-resource-nav-items";

interface FeatureLink {
  href: string;
  name: string;
  description?: string;
  icon: React.ReactElement;
}

interface MobileLink {
  groupName?: string;
  links?: FeatureLink[];
  name?: string;
  href?: string;
}

// Keyed by the nav item's href so a new Resources entry without an icon
// is a type error rather than a runtime `undefined`.
const RESOURCE_ICONS: Record<
  MarketingResourceNavItem["href"],
  React.ReactElement
> = {
  "/guides": <BookOpen className="stroke-foreground fill-teal-500/15" />,
  "/methodology": (
    <ScrollText className="stroke-foreground fill-violet-500/15" />
  ),
  "/case-studies": <Briefcase className="stroke-foreground fill-sky-500/15" />,
  "/research": <BarChart3 className="stroke-foreground fill-emerald-500/15" />,
  "/blog": <Newspaper className="stroke-foreground fill-amber-500/15" />,
  "/engineering": (
    <NotebookPen className="stroke-foreground fill-rose-500/15" />
  ),
};

function buildVisibleResourceLinks(
  visibleMarketingResources: readonly string[],
): FeatureLink[] {
  const visible = new Set(visibleMarketingResources);
  return MARKETING_RESOURCE_NAV_ITEMS.filter((link) =>
    visible.has(link.href),
  ).map((link) => ({
    ...link,
    icon: RESOURCE_ICONS[link.href]!,
  }));
}

const FEATURE_ICONS: Record<
  (typeof MARKETING_FEATURE_NAV_ITEMS)[number]["href"],
  React.ReactElement
> = {
  "/features#prompts": (
    <Lightbulb className="stroke-foreground fill-amber-500/15" />
  ),
  "/features#visibility": (
    <Activity className="stroke-foreground fill-emerald-500/15" />
  ),
  "/features#answers": (
    <MessagesSquare className="stroke-foreground fill-blue-500/15" />
  ),
  "/features#competitors": (
    <PieChart className="stroke-foreground fill-purple-500/15" />
  ),
  "/features#sources": <Link2 className="stroke-foreground fill-sky-500/15" />,
  "/features#content": (
    <FileText className="stroke-foreground fill-rose-500/15" />
  ),
  "/features#scans": <Gauge className="stroke-foreground fill-violet-500/15" />,
  "/features#integrations": (
    <LayoutGrid className="stroke-foreground fill-indigo-500/15" />
  ),
};

const features: FeatureLink[] = [
  {
    href: FEATURES_PATH,
    name: "All features",
    description: "Everything Ripenn measures, on one page",
    icon: <LayoutGrid className="stroke-foreground fill-indigo-500/15" />,
  },
  ...MARKETING_FEATURE_NAV_ITEMS.map((item) => ({
    ...item,
    icon: FEATURE_ICONS[item.href],
  })),
];

const VELOCITY_CALCULATOR_LINK: FeatureLink = {
  href: "/tools/content-velocity",
  name: "Content velocity",
  description: "Compare your blog cadence against a competitor",
  icon: <Gauge className="stroke-foreground fill-violet-500/15" />,
};

export type HeaderProps = {
  /** Base paths for containers with ≥1 MDX post (build-time). */
  visibleMarketingResources?: readonly string[];
  /** Whether the public content velocity calculator is enabled. */
  velocityCalculatorEnabled?: boolean;
};

export default function Header({
  visibleMarketingResources = [],
  velocityCalculatorEnabled = false,
}: HeaderProps) {
  const resourceLinksForNav = React.useMemo(() => {
    const base = buildVisibleResourceLinks(visibleMarketingResources);
    return velocityCalculatorEnabled
      ? [...base, VELOCITY_CALCULATOR_LINK]
      : base;
  }, [visibleMarketingResources, velocityCalculatorEnabled]);

  const mobileLinks: MobileLink[] = React.useMemo(() => {
    const links: MobileLink[] = [
      { groupName: "Product", links: features },
      { name: "Pricing", href: "/pricing" },
    ];
    if (resourceLinksForNav.length > 0) {
      links.splice(1, 0, {
        groupName: "Resources",
        links: resourceLinksForNav,
      });
    }
    return links;
  }, [resourceLinksForNav]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      role="banner"
      data-state={isMobileMenuOpen ? "active" : "inactive"}
      {...(isScrolled && { "data-scrolled": true })}
      className="fixed inset-x-0 top-0 z-50 bg-indigo-900/10"
    >
      <div
        className={cn(
          "absolute z-50 w-full transition-all duration-300",
          !isMobileMenuOpen && "max-lg:h-14 max-lg:overflow-hidden",
          isMobileMenuOpen && "bg-background/75 h-screen backdrop-blur",
        )}
      >
        <Container className="backdrop-blur">
          <div className="relative flex flex-wrap items-center justify-between px-6 lg:px-12 lg:py-5">
            <div className="max-lg:border-foreground/5 flex justify-between gap-8 max-lg:h-14 max-lg:w-full max-lg:border-b">
              <Link
                href="/"
                aria-label="home"
                className="flex items-center space-x-2"
              >
                <Logo priority />
              </Link>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={
                  isMobileMenuOpen == true ? "Close Menu" : "Open Menu"
                }
                className="relative z-20 -m-2.5 -mr-3 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 m-auto size-5 duration-200" />
                <X className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute inset-0 m-auto size-5 -rotate-180 scale-0 opacity-0 duration-200" />
              </button>
            </div>

            <div className="absolute inset-0 m-auto size-fit max-lg:hidden">
              <NavMenu
                resourceLinks={resourceLinksForNav}
                showResources={resourceLinksForNav.length > 0}
              />
            </div>
            {isMobileMenuOpen && (
              <div className="lg:hidden">
                <MobileMenu
                  closeMenu={() => setIsMobileMenuOpen(false)}
                  mobileLinks={mobileLinks}
                />
              </div>
            )}

            <div className="max-lg:in-data-[state=active]:mt-6 in-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                <Button asChild variant="outline" size="sm">
                  <Link href="/sign-in">
                    <span>Login</span>
                  </Link>
                </Button>
                <Button asChild size="sm">
                  <Link href="/signup">
                    <span>Get Started</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </header>
  );
}

const MobileMenu = ({
  closeMenu,
  mobileLinks,
}: {
  closeMenu: () => void;
  mobileLinks: MobileLink[];
}) => {
  return (
    <nav
      role="navigation"
      className="w-full [--color-muted:--alpha(var(--color-foreground)/5%)]"
    >
      <Accordion
        type="single"
        collapsible
        className="**:hover:no-underline -mx-4 mt-0.5 space-y-0.5"
      >
        {mobileLinks.map((link, index) => {
          if (link.groupName && link.links) {
            return (
              <AccordionItem
                key={index}
                value={link.groupName}
                className="group relative border-b-0"
              >
                <AccordionTrigger className="**:font-normal! data-[state=open]:bg-muted flex items-center justify-between px-4 py-3 text-lg">
                  {link.groupName}
                </AccordionTrigger>
                <AccordionContent className="pb-5">
                  <ul>
                    {link.links.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <Link
                          href={feature.href}
                          onClick={closeMenu}
                          className="grid grid-cols-[auto_1fr] items-center gap-2.5 px-4 py-2"
                        >
                          <div
                            aria-hidden
                            className="flex items-center justify-center *:size-4"
                          >
                            {feature.icon}
                          </div>
                          <div className="text-base">{feature.name}</div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            );
          }
          return null;
        })}
      </Accordion>
      {mobileLinks.map((link, index) => {
        if (link.name && link.href) {
          return (
            <Link
              key={index}
              href={link.href}
              onClick={closeMenu}
              className="group relative block py-4 text-lg"
            >
              {link.name}
            </Link>
          );
        }
        return null;
      })}
    </nav>
  );
};

const NavMenu = ({
  resourceLinks,
  showResources,
}: {
  resourceLinks: FeatureLink[];
  showResources: boolean;
}) => {
  return (
    <NavigationMenu className="**:data-[slot=navigation-menu-viewport]:bg-[color-mix(in_oklch,var(--color-muted)_25%,var(--color-background))] **:data-[slot=navigation-menu-viewport]:shadow-lg **:data-[slot=navigation-menu-viewport]:rounded-2xl **:data-[slot=navigation-menu-viewport]:top-4 [--color-muted:color-mix(in_oklch,var(--color-foreground)_5%,transparent)] [--viewport-outer-px:2rem] max-lg:hidden">
      <NavigationMenuList className="gap-3">
        <NavigationMenuItem value="product">
          <NavigationMenuTrigger>Product</NavigationMenuTrigger>
          <NavigationMenuContent className="origin-top pb-1.5 pl-1 pr-4 pt-1 backdrop-blur">
            <div className="min-w-xl grid w-full max-w-xl gap-1">
              <div className="bg-card grid gap-1 rounded-xl border p-1 pt-3">
                <span className="text-muted-foreground ml-2 text-xs">
                  Features
                </span>
                <ul>
                  {features.map((feature, index) => (
                    <ListItem
                      key={index}
                      href={feature.href}
                      title={feature.name}
                      description={feature.description}
                    >
                      {feature.icon}
                    </ListItem>
                  ))}
                </ul>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
        <NavigationMenuItem value="pricing">
          <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
            <Link href="/pricing">Pricing</Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
        {showResources && (
          <NavigationMenuItem value="resources">
            <NavigationMenuTrigger>Resources</NavigationMenuTrigger>
            <NavigationMenuContent className="origin-top pb-1.5 pl-1 pr-4 pt-1 backdrop-blur">
              <div className="min-w-xs grid w-full max-w-sm gap-1">
                <div className="bg-card grid gap-1 rounded-xl border p-1 pt-3">
                  <span className="text-muted-foreground ml-2 text-xs">
                    Resources
                  </span>
                  <ul>
                    {resourceLinks.map((item, index) => (
                      <ListItem
                        key={`${item.href}-${index}`}
                        href={item.href}
                        title={item.name}
                        description={item.description}
                      >
                        {item.icon}
                      </ListItem>
                    ))}
                  </ul>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

function ListItem({
  title,
  description,
  children,
  href,
  ...props
}: React.ComponentPropsWithoutRef<"li"> & {
  href: string;
  title: string;
  description?: string;
}) {
  return (
    <li {...props}>
      <NavigationMenuLink asChild className="rounded-lg">
        <Link href={href} className="grid grid-cols-[auto_1fr] gap-3.5">
          <div className="bg-card ring-foreground/10 relative flex size-10 items-center justify-center rounded border border-transparent shadow ring-1">
            {children}
          </div>
          <div className="space-y-0.5">
            <div className="text-foreground text-sm font-medium">{title}</div>
            <p className="text-muted-foreground line-clamp-1 text-xs">
              {description}
            </p>
          </div>
        </Link>
      </NavigationMenuLink>
    </li>
  );
}
